import mBaseModel         from '@/models/mBaseModel';
import UtilsData          from '@/utilities/UtilsData';
import mDealAcisLiability from '@/models/mDealAcisLiability';
import formatter          from '@/utilities/Formatter';
import moment             from 'moment';
import UtilsObject        from '@/utilities/UtilsObject';

import { ModelWithSmartProperties }    from '@/models/ModelWithSmartProperties';
import { ModelFormatters }             from '@/models/ModelFormatters';
import { ModelPropertyInfos }          from '@/models/ModelPropertyInfos';

export default class mDealAcis extends ModelWithSmartProperties {
  static DefaultLiabilityBondA() {
    return { BondName: null, OfferedPct: NaN, Balance: NaN, Premium: NaN, CallMultiplier: NaN };
  }

  static DefaultLiability() {
    return [
      { BondName: null },
    ];
  }

  static Defaults = () => {
    const now = new Date();
    return {
      Name:      'ACIS 2022-AA',
      Structure: {
        EffectiveDate: parseInt(
          moment()
            .year(now.getFullYear())
            .month(now.getMonth())
            .date(1)
            .format('YYYYMMDD'), 10),
        MaturityDate:  parseInt(
          moment()
            .year(now.getFullYear())
            .month(now.getMonth() + 1)
            .date(25)
            .format('YYYYMMDD'), 10),
        MonthsToCall:  60,
      },
      Liability: mDealAcis.DefaultLiability(),
    };
  };

  static vocDbToLabel(dbName = null) {
    const ats = Object.freeze({
      Name:      'ACIS name',
      Structure: {
        EffectiveDate: 'Effective Date',
        MaturityDate:  'Maturity Date',
        MonthsToCall:  'Months To Call',
      },
      Liability: 'ACIS Liability Description',
    });
    if (dbName) {
      return UtilsObject.getByPath(ats, dbName);
    }
    return ats;
  }

  constructor(rawModelFields = {}) {    
    super(rawModelFields);
    this.ats = {
      ...mDealAcis.Defaults(),
    };

    const datePropertyInfo = {
      ToUserDataFormatter: ModelFormatters.ToUserData.NumberAsDate(),
      // ToModelDataFormatter: ModelFormatters.ToModelData.DateAsNumber()
    };

    this.defineModelProperty('Name', ModelPropertyInfos.Text);
    this.defineModelProperty('Structure.EffectiveDate', datePropertyInfo);
    this.defineModelProperty('Structure.MaturityDate', datePropertyInfo);
    this.defineModelProperty('Structure.MonthsToCall', ModelPropertyInfos.IntegerNumber);

    this.setRawFields({
      ...this.ats,
      ...rawModelFields
    });
  }

  setRawFields(rawModelFields = {}) {
    for (let prop of Object.keys(rawModelFields)) {
      if (this.ats.hasOwnProperty(prop)) {
        switch (prop) {
          case 'Liability':
            // Starts with the default padding liability item
            this.ats[prop] = [
              new mDealAcisLiability(mDealAcis.DefaultLiabilityBondA())
            ];
            for (let [i, obj] of Object.entries(rawModelFields[prop])) {
              this.ats[prop].push(new mDealAcisLiability(obj));
            }
            break;
          case 'Structure':
            for (let [propName, propValue] of Object.entries(rawModelFields[prop])) {
              if (propName in this.ats[prop]) {
                this.ats[prop][propName] = undefined;
                ModelWithSmartProperties.setPropertyByPath(this, `${prop}.${propName}$`, propValue);
              }
            }
            break;
          default: {
            ModelWithSmartProperties.setPropertyByPath(this, `${prop}$`, rawModelFields[prop]);
          }
        }
      }
    }
    return this;
  }

  getRawFieldsFormattedForBackEnd() {
    const res = {
      Name: this.Name$,
      Structure: {
        EffectiveDate: ModelFormatters.ToModelData.DateAsNumber()(null, this.Structure.EffectiveDate$),
        MaturityDate: ModelFormatters.ToModelData.DateAsNumber()(null, this.Structure.MaturityDate$),
        MonthsToCall: this.Structure.MonthsToCall$,
      },
    };
    res.Liability = [];
    for (let [i, model] of Object.entries(this.ats.Liability)) {
      // Never expose the default padding liability item
      if (i == 0) {
        continue;
      }
      res.Liability.push(model.getRawFieldsFormattedForBackEnd());
    }
    return res;
  }

  calculateAutoFields(idx, stacrLiabilitiyItem) {
    if (stacrLiabilitiyItem == null || idx >= this.ats.Liability.length) {
      return;
    }
    const { Balance$: balance = 0.0, BondName$: bondName = null } = stacrLiabilitiyItem;
    const { OfferedPct$: offeredPct = 0.0 } = this.ats.Liability[idx];
    this.ats.Liability[idx].Balance$ = balance * offeredPct / 100;
    this.ats.Liability[idx].BondName$ = bondName != null ? this.ats.Liability[idx].BondName$ : null;
  }

  validate() {
    const ats                    = this.getRawFieldsFormattedForBackEnd();
    this._localValidationStatus  = true;
    this._localValidationResults = [];

    const flattenObjectProperties = (obj, prefix) => Object.entries(obj[prefix])
      .map(([_propName, _propValue]) => [`${prefix}.${_propName}`, _propValue]);
    const flattenArrayProperties = (array, prefix) => array.map(
      (_value, _idx) => [`${prefix}.${_idx}`, _value]);

    const entriesToValidate = [
      ...flattenObjectProperties(ats, 'Structure'),
      ...Object.entries(ats)
        .filter(([_name, _value]) => (!/Structure|Liability/.test(_name))),
    ];

    const liabilityItems = flattenArrayProperties(this.ats.Liability, 'Liability');

    // Validate liabilities models and grab validation errors with the keys adjustments
    for (let [key, value] of liabilityItems) {
      // Do not validate the first padding row
      if (key.startsWith('Liability.0')) {
        continue;
      }
      const validationStatus = value.validate();
      validationStatus.forEach(validationError => {
        this._localValidationResults.push({
          ...validationError,
          fieldSysName: `${key}.${validationError.fieldSysName}`
        });
      });
    }

    const createErrorDescription = (msg, fieldName, fieldSysName) => {
      return {
        modelName: 'ACIS',
        field: fieldName,
        msg,
        fieldSysName
      };
    };

    // 'Effective Date' is a date, where 1/1/2010 < Date… and defines the date on whuch the ACIS Contract takes effect… NO default.													
    // 'Maturity Date' is a date, where 'Effective Date' <  'Maturity Date'… and defines the date on which the ACIS Contract matures… NO default.													
    const effectiveDate = this.Structure.EffectiveDate$;
    const maturityDate = this.Structure.MaturityDate$;

    if (!(effectiveDate.getTime() < maturityDate.getTime())) {
      this._localValidationResults.push(createErrorDescription(
        'Effective < Maturity',
        mDealAcis.vocDbToLabel('Structure.EffectiveDate'),
        'Structure.EffectiveDate'));
    }

    // 'Months to Call' is an integer, where 0 < Months < 360
    // and defines the earliest period in which the ACIS Contract may be called (default is 60).
    const monthsToCall = this.Structure.MonthsToCall$;
    if (!(monthsToCall > 0 && monthsToCall < 360)) {
      this._localValidationResults.push(createErrorDescription(
        '0 < Months To Call < 360',
        mDealAcis.vocDbToLabel('Structure.MonthsToCall'),
        'Structure.MonthsToCall'));
    }

    for (let [fieldSysName, fValue] of entriesToValidate) {
      const fieldName = mDealAcis.vocDbToLabel(fieldSysName)
      const error = createErrorDescription('', fieldName, fieldSysName);
      switch (fieldSysName) {
        case 'Name':
          error.msg = 'Name mask is: ACIS YYYY-[a-z0-9]{3}*';
          if (!/ACIS(\s[a-zA-Z-]+)*\s\d{4}-[0-9a-zA-Z]{3}[0-9a-zA-Z-]*$/gm.test(fValue)) {
            this._localValidationResults.push(error);
          }
          break;
        default:
          error.msg = 'Field is required.';
          if (typeof fValue === 'number') {
            if (isNaN(fValue) || fValue == null) {
              this._localValidationResults.push(error);
            }
          } else {
            if (UtilsData.isEmpty(fValue)) {
              this._localValidationResults.push(error);
            } else if (fValue === 'Invalid date') {
              error.msg = 'Invalid Date.';
              this._localValidationResults.push(error);
            }
          }
      }
    }
    this._localValidationStatus = this._localValidationResults.length === 0;
    return this._localValidationStatus;
  }
}
